import React, { useState, useEffect } from 'react';
import { TrendingUp, Building2, Wallet } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { incomeAPI } from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';
import toast from 'react-hot-toast';

const fmt = (n) => Number(n || 0).toLocaleString('fr-MA');

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:10, padding:'10px 14px', fontSize:13 }}>
      <div style={{ color:'var(--text-muted)', marginBottom:4 }}>{label}</div>
      <div style={{ color:'var(--gold)', fontWeight:700 }}>{fmt(payload[0].value)} MAD</div>
    </div>
  );
}

export default function Income() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t } = useLanguage();

  useEffect(() => {
    incomeAPI.get()
      .then(res => setData(res.data.data))
      .catch(() => toast.error('Failed to load income data'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'60vh' }}>
      <span className="spinner" style={{ width:32, height:32, borderWidth:3 }} />
    </div>
  );

  const { this_month = 0, all_time = 0, by_property = [], trend = [] } = data || {};
  const best = trend.reduce((max, m) => m.total > max ? m.total : max, 0);
  const currentMonth = new Date().toLocaleString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div>
      <div style={{ marginBottom:28 }}>
        <h1 style={{ fontSize:28, fontWeight:800, marginBottom:4 }}>{t('income.title')}</h1>
        <p style={{ color:'var(--text-secondary)', fontSize:14 }}>{t('income.subtitle')}</p>
      </div>

      <div className="grid-3" style={{ marginBottom:28 }}>
        {[
          { label: t('income.thisMonth'), value: `${fmt(this_month)} MAD`, icon: TrendingUp, color:'var(--gold)' },
          { label: t('income.allTime'), value: `${fmt(all_time)} MAD`, icon: Wallet, color:'var(--accent-light)' },
          { label: t('income.bestMonth'), value: `${fmt(best)} MAD`, icon: Building2, color:'var(--success)' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="stat-card">
            <div style={{ width:40, height:40, borderRadius:10, background:`${color}20`, display:'flex', alignItems:'center', justifyContent:'center', marginBottom:12 }}>
              <Icon size={18} color={color} />
            </div>
            <div className="stat-number" style={{ color, fontSize:22 }}>{value}</div>
            <div className="stat-label">{label}</div>
          </div>
        ))}
      </div>

      <div className="card" style={{ marginBottom:24 }}>
        <h3 style={{ fontSize:15, fontWeight:700, marginBottom:20 }}>{t('income.chartTitle')}</h3>
        {trend.every(m => m.total === 0) ? (
          <div style={{ textAlign:'center', padding:'40px 0', color:'var(--text-muted)', fontSize:14 }}>
            {t('income.noData')}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={trend} margin={{ top:4, right:8, left:0, bottom:0 }}>
              <CartesianGrid vertical={false} stroke="var(--border)" strokeDasharray="3 3" />
              <XAxis dataKey="month" tick={{ fill:'var(--text-muted)', fontSize:11 }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={v => v === 0 ? '0' : `${(v/1000).toFixed(0)}k`} tick={{ fill:'var(--text-muted)', fontSize:11 }} axisLine={false} tickLine={false} width={36} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill:'rgba(108,99,255,0.08)' }} />
              <Bar dataKey="total" fill="var(--accent)" radius={[6,6,0,0]} maxBarSize={40} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        <div style={{ padding:'20px 24px 0' }}>
          <h3 style={{ fontSize:15, fontWeight:700, marginBottom:16 }}>{t('income.byProperty', currentMonth)}</h3>
        </div>
        {by_property.length === 0 ? (
          <div className="empty-state" style={{ paddingBottom:48 }}>
            <Building2 size={40} />
            <h3>{t('income.noProperties')}</h3>
            <p>{t('income.noPropertiesDesc')}</p>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>{t('table.property')}</th>
                <th>{t('table.city')}</th>
                <th>{t('table.bookingsThisMonth')}</th>
                <th style={{ textAlign:'right' }}>{t('table.thisMonth')}</th>
                <th style={{ textAlign:'right' }}>{t('table.allTime')}</th>
              </tr>
            </thead>
            <tbody>
              {by_property.map((p, i) => (
                <tr key={i}>
                  <td style={{ fontWeight:500 }}>{p.property_name}</td>
                  <td style={{ color:'var(--text-secondary)' }}>{p.city}</td>
                  <td>
                    <span className={`badge ${p.bookings_this_month > 0 ? 'badge-accent' : 'badge-warning'}`}>
                      {p.bookings_this_month}
                    </span>
                  </td>
                  <td style={{ textAlign:'right', fontWeight:700, color:'var(--gold)' }}>{fmt(p.this_month)} MAD</td>
                  <td style={{ textAlign:'right', color:'var(--text-secondary)' }}>{fmt(p.all_time)} MAD</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
